/**
 * Registers a genuine Quill BlockEmbed blot for the product-embed card.
 *
 * dangerouslyPasteHTML alone (the pattern used by insertVideoTag elsewhere in
 * this module) isn't enough here:
 * Quill's HTML->Delta conversion only preserves nodes it recognizes as a
 * registered format. A plain <div class="ql-product-embed"> has no matching
 * Blot, so Quill silently unwraps it — dropping the div itself (and its
 * contenteditable/data-product-id attributes) and merging the inner <a>/<img>
 * content into whatever surrounding text block happens to be there. Extending
 * BlockEmbed the same way WysiwygImageFormatUtils extends the built-in Image
 * format is what makes the whole card survive as one atomic unit through
 * every HTML<->Delta round trip (initial load, editor re-render, Preview tab).
 */

interface BlockEmbedInstance {
    domNode: HTMLElement;
}

interface BlockEmbedStatic {
    new (...args: never[]): BlockEmbedInstance;
    create(value?: unknown): HTMLElement;
}

interface QuillLike {
    import(name: string): unknown;
    register(formats: Record<string, unknown>, overwrite?: boolean): void;
}

interface ProductEmbedBlotValue {
    productId: string;
    html: string;
}

const FORMAT_NAME = "productEmbed";

let registered = false;

class WysiwygProductEmbedBlotUtils {
    public static readonly formatName = FORMAT_NAME;

    public static register(Quill: QuillLike | null | undefined): void {
        if (registered || !Quill) return;

        const BlockEmbed = Quill.import("blots/block/embed") as BlockEmbedStatic | null;
        if (!BlockEmbed) return;

        class ProductEmbedBlot extends BlockEmbed {
            public static blotName = FORMAT_NAME;
            public static tagName = "div";
            public static className = "ql-product-embed";

            public static override create(value: ProductEmbedBlotValue): HTMLElement {
                const node = super.create(value) as HTMLElement;
                node.setAttribute("contenteditable", "false");
                node.setAttribute("data-product-id", value.productId);
                node.innerHTML = value.html;
                return node;
            }

            public static value(node: HTMLElement): ProductEmbedBlotValue {
                return {
                    productId: node.getAttribute("data-product-id") || "",
                    html: node.innerHTML,
                };
            }
        }

        Quill.register({ [`formats/${FORMAT_NAME}`]: ProductEmbedBlot }, true);
        registered = true;
    }
}

export { WysiwygProductEmbedBlotUtils };
export type { ProductEmbedBlotValue };
